/**
 * useModuleCapabilities — read the active module's capability block (ADR-0068).
 *
 * The /api/discovery endpoint on each module surfaces a CapabilitiesSpec
 * (see platform_sdk.discovery.DiscoveryResponse) that is the union of the
 * module's ``module.yaml`` (declared CAPABLE-OF) and the deployment-time
 * Helm values (declared ENABLED-FOR-THIS-DEPLOYMENT). The sidebar nav and
 * other capability-gated UI surfaces project off the same object.
 *
 * Server-side authorization is the actual security boundary (ADR-0062
 * WorkbenchScoped, ADR-0063 AgentChatAccess, etc.). This hook only avoids
 * dead links — it should never be the only gate.
 */

import { useQuery } from '@tanstack/react-query'
import type { DiscoveryResponse, ModuleCapabilities } from '../modules/shared/types'

/** Safe baseline used when a module hasn't shipped a capability block yet.
 *
 * Defaults follow the principle of least exposure — Portal on (the module
 * is at least viewable for the customer), every other surface off until
 * explicitly enabled per ADR-0068.
 */
export const DEFAULT_CAPABILITIES: ModuleCapabilities = {
  portal: { enabled: true },
  api: { enabled: false },
  ai_assistant: { enabled: false, rag_corpus: null },
  workbench: {
    enabled: false,
    jupyter: false,
    prefect: false,
    mlflow: false,
    gitea: false,
    dbt: false,
    pipeline: false,
    dashboards: false,
    code_assistant: false,
  },
}

async function fetchDiscovery(moduleName: string): Promise<DiscoveryResponse> {
  const baseUrl =
    typeof window !== 'undefined' && window.location.origin
      ? `${window.location.origin}/api/modules/${moduleName}`
      : `http://localhost:5000/api/modules/${moduleName}`
  const res = await fetch(`${baseUrl}/api/discovery`, { credentials: 'include' })
  if (!res.ok) {
    throw new Error(`Discovery failed: ${res.status} ${res.statusText}`)
  }
  return res.json() as Promise<DiscoveryResponse>
}

export interface UseModuleCapabilitiesResult {
  capabilities: ModuleCapabilities
  isLoading: boolean
  /** True when the response was missing a ``capabilities`` block and defaults were applied. */
  usingDefaults: boolean
}

/** Fetch and project capabilities for a module name (null = no active module). */
export function useModuleCapabilities(
  moduleName: string | null | undefined,
): UseModuleCapabilitiesResult {
  const { data, isLoading } = useQuery({
    queryKey: ['module-discovery', moduleName],
    queryFn: () => fetchDiscovery(moduleName as string),
    enabled: !!moduleName,
    staleTime: 5 * 60 * 1000,
  })

  if (!moduleName) {
    return { capabilities: DEFAULT_CAPABILITIES, isLoading: false, usingDefaults: true }
  }
  if (isLoading || !data) {
    return { capabilities: DEFAULT_CAPABILITIES, isLoading: true, usingDefaults: true }
  }
  if (!data.capabilities) {
    return { capabilities: DEFAULT_CAPABILITIES, isLoading: false, usingDefaults: true }
  }
  return { capabilities: data.capabilities, isLoading: false, usingDefaults: false }
}
